import gql from 'graphql-tag';

export const OFFER_PROMO_FRAGMENT = gql`
  fragment OfferPromoFragment on OfferPromo {
    codeSuffix
    codePrefix
    disabled
    flatPayout
    id
    name
    offerId
    payoutType
    percentPayout
    prefixType
    priceRuleAmount
    priceRuleType
    source
    specialLimitNewCustomersOnly
    specialLimitOnePerSale
    specialLimitUsageCapAmount
    specialLimitUsageCapEnabled
    status
    defaultPayoutId
    usageLimitAmount
    usageLimitRule
    providerMetadata {
      productCollections {
        id
        title
      }
      discountCombination {
        orderDiscounts
        productDiscounts
        shippingDiscounts
      }
    }
    isSubscription
    isUngrouped
    isOneTime
    recurringCycleLimit
    discountCodeGId
    startDate
    endDate
    multiShopifyEnabled
    isSecureCodes
    connectedClientMetadata {
      advertiserId
      priceRuleType
      priceRuleAmount
      status
      discountCodeGid
    }
  }
`;

// Lightweight base fragment without affiliate data
export const OFFER_BASE_FRAGMENT = gql`
  fragment OfferBaseFragment on Offer {
    createdDate
    description
    expirationDate
    expired
    id
    imageUrl
    icon
    programId
    showSendConfirmationScreen
    isNewFlow
    offerAutomationConfig {
      id
      isActive
      automationType
      automationStartDate
      tierAggregationDuration
      showInCreatorPortal
    }
    isReadOnly
    isPromoLink
    isLandingPageEnabled
    landingPageUrl
    showPayoutsOnCreatorPortal
    hideOfferFromCreators
    payouts {
      id
      payoutType
      label
      flatPayout
      percentPayout
      offerId
      isMemberAdded
      createdDate
      automationThreshold
      offerAutomationConfigId
      offerPayoutRules {
        id
        operator
        collections {
          id
          name
          advertiserId
          operator
        }
        duration {
          startDate
          endDate
        }
        flatPayout
        orderSubtotal {
          value
          operator
        }
        percentPayout
        products {
          id
          name
          type
          imageUrl
          advertiserId
          operator
        }
        recurringLimit {
          isLimitPayoutToFirstOrder
          isPayoutAppliedToAllRecurringOrders
          limitPayoutToMultipleRecurringOrder
        }
      }
    }
    name
    type
    archivedDate
    archivedUser {
      name
      email
    }
    boosts {
      activeEndDate
      activeStartDate
      boostMetadata
      boostType
      createdDate
      discountOfferId #can be used to redirect to the boost offer
      hideInCreatorPortal
      id
      isActive
      label
      maxLimit
      requireApproval
      triggerType
      triggerValue
      updatedDate
      discountOffer {
        promos {
          ...OfferPromoFragment
        }
      }
      boostEligibility {
        id
        createdDate
        eligibilityType
        memberIds
        updatedDate
        advancedSelectionMetadata
        advancedSelectionOperator
      }
    }
  }
  ${OFFER_PROMO_FRAGMENT}
`;

export const OFFER_FRAGMENT = gql`
  fragment OfferFragment on Offer {
    createdDate
    description
    expirationDate
    expired
    id
    imageUrl
    icon
    programId
    showSendConfirmationScreen
    isNewFlow
    offerAutomationConfig {
      id
      isActive
      automationType
      automationStartDate
      tierAggregationDuration
      showInCreatorPortal
    }
    isReadOnly
    isPromoLink
    isLandingPageEnabled
    landingPageUrl
    showPayoutsOnCreatorPortal
    hideOfferFromCreators
    payouts {
      id
      payoutType
      label
      flatPayout
      percentPayout
      offerId
      isMemberAdded
      createdDate
      automationThreshold
      offerAutomationConfigId
      offerPayoutRules {
        id
        operator
        collections {
          id
          name
          advertiserId
          operator
        }
        duration {
          startDate
          endDate
        }
        flatPayout
        orderSubtotal {
          value
          operator
        }
        percentPayout
        products {
          id
          name
          type
          imageUrl
          advertiserId
          operator
        }
        recurringLimit {
          isLimitPayoutToFirstOrder
          isPayoutAppliedToAllRecurringOrders
          limitPayoutToMultipleRecurringOrder
        }
        customerType
      }
    }
    links {
      affiliates {
        affiliateId
        offerPayoutId
        affiliateDeeplinks {
          id
          affiliateDeepLinkUrl
          url
          label
          createdDate
          deletedDate
          shortLink
        }
        affiliate {
          email
          memberId
          name
          profilePicture
          status
          deletedDate
        }
        id
        deletedDate
        createdDate
        updateLog
        deeplinkUrl
        affiliateShortLink
        offerAutomationConfigId
        boostDataForAffiliateOffer {
          boost {
            id
            label
            boostType
            status
            isActive
          }
          boostRewardsForAffiliate {
            boostId
            createdDate
            deletedDate
            id
            isActive
            secureCode {
              externalDiscountCode
              isRedeemed
            }
            secureCodeId
            status
            updatedDate
            affiliateId
            shopifyOrderEvent {
              orderStatusUrl
            }
            boostRewardMetadata
          }
        }
      }
      defaultPayoutId
      affiliateOfferLinkGenerated
      conversionTrackingType
      conversionType
      flatPayout
      id
      payoutType
      percentPayout
      pixelCode
      postbackUrl
      status
      url
      utmFields
      domains
      creatorDeeplink
      allowMultipleConversions
      thirdPartyTracking
      trackPayoutInAspire
      isThirdPartyIntegration
      isCreatorDeepLinkEnabled: creatorDeeplink
      shopifyRedirectUrl
    }
    promos {
      id
      affiliates {
        affiliateId
        offerPayoutId
        affiliate {
          email
          memberId
          name
          profilePicture
          status
          deletedDate
        }
        endDate
        externalDiscountCode
        externalDiscountCodeGId
        id
        startDate
        deletedDate
        status
        providerMetadata
        createdDate
        offerAutomationConfigId
        boostDataForAffiliateOffer {
          boost {
            id
            label
            boostType
            status
            isActive
          }
          boostRewardsForAffiliate {
            boostId
            createdDate
            deletedDate
            id
            isActive
            secureCode {
              externalDiscountCode
              isRedeemed
            }
            secureCodeId
            status
            updatedDate
            affiliateId
            shopifyOrderEvent {
              orderStatusUrl
            }
            boostRewardMetadata
          }
        }
      }
      codeSuffix
      codePrefix
      disabled
      flatPayout
      id
      name
      offerId
      payoutType
      percentPayout
      prefixType
      priceRuleAmount
      priceRuleType
      source
      specialLimitNewCustomersOnly
      specialLimitOnePerSale
      specialLimitUsageCapAmount
      specialLimitUsageCapEnabled
      status
      defaultPayoutId
      usageLimitAmount
      usageLimitRule
      providerMetadata {
        productCollections {
          id
          title
        }
        discountCombination {
          orderDiscounts
          productDiscounts
          shippingDiscounts
        }
      }
      isSubscription
      isUngrouped
      isOneTime
      recurringCycleLimit
      discountCodeGId
      startDate
      endDate
      multiShopifyEnabled
      isSecureCodes
      connectedClientMetadata {
        advertiserId
        priceRuleType
        priceRuleAmount
        status
        discountCodeGid
      }
    }
    name
    type
    archivedDate
    archivedUser {
      name
      email
    }
    boosts {
      activeEndDate
      activeStartDate
      boostMetadata
      boostType
      createdDate
      discountOfferId #can be used to redirect to the boost offer
      hideInCreatorPortal
      id
      isActive
      label
      maxLimit
      requireApproval
      triggerType
      triggerValue
      updatedDate
      discountOffer {
        promos {
          ...OfferPromoFragment
        }
      }
      boostEligibility {
        id
        createdDate
        eligibilityType
        memberIds
        updatedDate
        advancedSelectionMetadata
        advancedSelectionOperator
      }
    }
    isTierPerformanceSynced
  }
  ${OFFER_PROMO_FRAGMENT}
`;
